import { authClient } from "@/auth-client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, createFileRoute, useRouter } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import { z } from "zod";

const verifyOtpSearchSchema = z.object({
  email: z.string().email().min(1),
  redirectTo: z.string().optional(),
});

export const Route = createFileRoute("/(auth)/code")({
  component: VerifyOtpComponent,
  validateSearch: zodValidator(verifyOtpSearchSchema),
  errorComponent: () => {
    return <Navigate to="/login" params={{ error: "invalid_params" }} />;
  },
});

function VerifyOtpComponent() {
  const { email, redirectTo } = Route.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();

  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleVerifyOtp = async () => {
    if (!email) {
      setError("El email es requerido");
      return;
    }

    if (otp.length !== 6) {
      setError("Por favor ingresa el código completo de 6 dígitos");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const { error: verifyError } = await authClient.signIn.emailOtp({
        email,
        otp,
      });

      if (verifyError) {
        throw new Error(verifyError.message);
      }

      await queryClient.invalidateQueries({ refetchType: "all" });
      //await queryClient.invalidateQueries({ queryKey: ["session"] });
      /* TODO
       * Doesn't work without refetchType: "all".
       * If getSession() returns null, it seems that the query does not refetch in _authenticated.tsx.
       * This results in a redirect to login.tsx, which then redirects to _authenticated.tsx,
       * which then redirects to _authenticated/index.tsx, which then redirects to $companyId/index.tsx.
       */
      router.history.push(redirectTo ?? "/");
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError(err instanceof Error ? err.message : "Failed to verify OTP");
      setOtp("");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-100 dark:bg-slate-900 flex flex-col">
      {/* Mobile fullscreen layout */}
      <div className="flex-1 flex items-center justify-center md:p-8">
        <div className="w-full max-w-md">
          <Card className="h-screen md:h-auto md:shadow-xl border-0 md:border bg-white dark:bg-slate-800 md:bg-white/95 md:dark:bg-slate-800/95 md:backdrop-blur md:rounded-lg rounded-none">
            <CardHeader className="text-center space-y-6 pb-4 px-6 md:px-8 pt-8 md:pt-6">
              {/* Logo */}
              <div className="flex justify-center">
                <img src="/logo.svg" alt="HSE Chain" className="h-12 w-auto" />
              </div>

              {/* Title and Description */}
              <div className="space-y-2">
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Verifica tu email
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-400">
                  Ingresa el código de verificación enviado a tu email
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 px-8">
              {/* OTP Input */}
              <div className="flex flex-col items-center space-y-4">
                {error && (
                  <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-md border border-red-200 dark:border-red-800 max-w-fit">
                    {error}
                  </div>
                )}

                <div className="pb-6">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(value) => {
                      setOtp(value);
                      setError(null);
                    }}
                    onComplete={handleVerifyOtp}
                    disabled={loading}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
